// Pure SVG-polygon math for an N-axis radar/spider chart. Axis 0 starts at
// the top (12 o'clock, angle = -PI/2) and axes go clockwise — matches the
// reading direction the pillar bars below it already use.
export function computeRadarPoints(values, maxValues, size = 220, radiusFactor = 0.62) {
  const n = values.length;
  const center = size / 2;
  const radius = (size / 2) * radiusFactor; // leaves room for axis labels outside the plot
  const axisPoints = values.map((v, i) => {
    const angle = -Math.PI / 2 + (i * 2 * Math.PI) / n;
    const max = maxValues[i] || 0;
    const ratio = max > 0 ? Math.max(0, Math.min(v / max, 1)) : 0;
    const x = center + radius * ratio * Math.cos(angle);
    const y = center + radius * ratio * Math.sin(angle);
    const labelX = center + (radius + 22) * Math.cos(angle);
    const labelY = center + (radius + 22) * Math.sin(angle);
    return { x, y, labelX, labelY, angle };
  });
  const points = axisPoints.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  return { points, axisPoints, center, radius };
}

// Optional two-line label: split after a hyphen or at a space, nearest the
// middle, only when the label is longer than `max` characters.
export function splitLabel(label, max = 10) {
  const s = String(label ?? "");
  if (s.length <= max) return [s];
  const cuts = [];
  for (let i = 1; i < s.length - 1; i++) {
    if (s[i] === " ") cuts.push({ at: i, skip: 1 });
    else if (s[i] === "-") cuts.push({ at: i + 1, skip: 0 });
  }
  if (!cuts.length) return [s];
  const best = cuts.reduce((a, b) => (Math.abs(b.at - s.length / 2) < Math.abs(a.at - s.length / 2) ? b : a));
  return [s.slice(0, best.at), s.slice(best.at + best.skip)];
}

function wrapped(label, x) {
  const lines = splitLabel(label);
  if (lines.length === 1) return label;
  return lines.map((ln, j) => (
    <tspan key={j} x={x} dy={j === 0 ? "-0.55em" : "1.1em"}>{ln}</tspan>
  ));
}

// "You" filled polygon, optional dashed "benchmark" (e.g. top-3 average)
// outline for comparison — two series max, both status-neutral (brand blue
// vs. neutral gray), never one hue per pillar (spec §2).
export function PillarRadar({ pillars, pillarMax, pillarKeys, pillarLabels, benchmarkPillars, size = 220, padX = 0, padY = 0, labelSize = 12, radiusFactor = 0.62, wrapLabels = false, title }) {
  const values = pillarKeys.map((k) => pillars[k] ?? 0);
  const maxValues = pillarKeys.map((k) => pillarMax[k]);
  const { points, axisPoints, center } = computeRadarPoints(values, maxValues, size, radiusFactor);

  const benchmarkPoints = benchmarkPillars
    ? computeRadarPoints(pillarKeys.map((k) => benchmarkPillars[k] ?? 0), maxValues, size, radiusFactor).points
    : null;

  // Ring guides at 25/50/75/100% for scale reference, drawn as plain
  // polygons at each fraction of the radius.
  const rings = [0.25, 0.5, 0.75, 1].map((frac) =>
    computeRadarPoints(maxValues.map((m) => m * frac), maxValues, size, radiusFactor).points
  );

  return (
    <svg
      width="100%"
      height={padX || padY ? undefined : size}
      viewBox={`${-padX} ${-padY} ${size + 2 * padX} ${size + 2 * padY}`}
      style={padX || padY ? { maxWidth: size + 2 * padX, aspectRatio: `${size + 2 * padX} / ${size + 2 * padY}` } : { maxWidth: size }}
      className="mx-auto overflow-visible"
      {...(title ? { role: "img" } : { "aria-hidden": "true" })}
    >
      {title && <title>{title}</title>}
      {rings.map((ringPoints, i) => (
        <polygon key={i} points={ringPoints} fill="none" stroke="hsl(210,20%,90%)" strokeWidth={1} />
      ))}
      {axisPoints.map((p, i) => (
        <line key={i} x1={center} y1={center} x2={p.labelX - (p.labelX - center) * 0.08} y2={p.labelY - (p.labelY - center) * 0.08} stroke="hsl(210,20%,90%)" strokeWidth={1} />
      ))}
      {benchmarkPoints && (
        <polygon points={benchmarkPoints} fill="none" stroke="hsl(215,16%,55%)" strokeWidth={1.5} strokeDasharray="4,3" />
      )}
      <polygon points={points} fill="hsl(217,100%,59%)" fillOpacity={0.22} stroke="hsl(217,100%,59%)" strokeWidth={2} />
      {axisPoints.map((p, i) => (
        <text
          key={i}
          x={p.labelX}
          y={p.labelY}
          textAnchor={Math.abs(p.labelX - center) < 4 ? "middle" : p.labelX > center ? "start" : "end"}
          dominantBaseline="middle"
          className="fill-muted-foreground"
          style={{ fontSize: labelSize }}
        >
          {wrapLabels ? wrapped(pillarLabels[pillarKeys[i]], p.labelX) : pillarLabels[pillarKeys[i]]}
        </text>
      ))}
    </svg>
  );
}
